/**
 * All instants are stored as UTC ISO-8601 strings ("2026-07-27T16:30:00.000Z").
 * All calendar dates are stored as "YYYY-MM-DD" with no timezone — a job on the
 * 5th is on the 5th regardless of where the server runs.
 *
 * Conversions between the two must go through the organization's timezone, never
 * the runtime's, because Workers always run in UTC.
 */

export type IsoInstant = string;
export type CalendarDate = string;

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function nowIso(): IsoInstant {
  return new Date().toISOString();
}

export function isCalendarDate(value: string): value is CalendarDate {
  if (!DATE_RE.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

export function assertCalendarDate(value: string, label = 'date'): asserts value is CalendarDate {
  if (!isCalendarDate(value)) throw new Error(`${label} must be YYYY-MM-DD, got "${value}"`);
}

/** Today's calendar date in the given IANA timezone. */
export function todayIn(timeZone: string, at: Date = new Date()): CalendarDate {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(at);
  return parts;
}

export function addDays(date: CalendarDate, days: number): CalendarDate {
  assertCalendarDate(date);
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function addMonths(date: CalendarDate, months: number): CalendarDate {
  assertCalendarDate(date);
  const d = new Date(`${date}T00:00:00Z`);
  const targetDay = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + months);
  // Clamp: Jan 31 + 1 month => Feb 28/29, not Mar 3.
  const daysInTarget = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(targetDay, daysInTarget));
  return d.toISOString().slice(0, 10);
}

export function daysBetween(from: CalendarDate, to: CalendarDate): number {
  assertCalendarDate(from, 'from');
  assertCalendarDate(to, 'to');
  const ms = new Date(`${to}T00:00:00Z`).getTime() - new Date(`${from}T00:00:00Z`).getTime();
  return Math.round(ms / 86_400_000);
}

/** 0 = Sunday … 6 = Saturday, matching JS getUTCDay. */
export function dayOfWeek(date: CalendarDate): number {
  assertCalendarDate(date);
  return new Date(`${date}T00:00:00Z`).getUTCDay();
}

export function compareDates(a: CalendarDate, b: CalendarDate): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Sunday-start week containing `date`. */
export function startOfWeek(date: CalendarDate): CalendarDate {
  const dow = dayOfWeek(date);
  return addDays(date, -dow);
}

export function startOfMonth(date: CalendarDate): CalendarDate {
  assertCalendarDate(date);
  return `${date.slice(0, 7)}-01`;
}
